/**
 * useThrottledCallback — Invoke a callback at most once per interval.
 *
 * Returns a stable function that, when called, invokes `callback`
 * immediately if `intervalMs` has elapsed since the last invocation
 * — otherwise it schedules a single trailing call so the last set of
 * arguments is not lost. Useful for `scroll` / `resize` / `pointermove`
 * handlers that would otherwise re-render the parent dozens of times
 * per second.
 *
 * Complementary to {@link useDebouncedValue}: debounce delays until
 * input quiets down, throttle rate-limits while input keeps flowing.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useRef } from "react";

export function useThrottledCallback<TArgs extends readonly unknown[]>(
  callback: (...args: TArgs) => void,
  intervalMs: number,
): (...args: TArgs) => void {
  const latestCallback = useRef(callback);
  latestCallback.current = callback;
  const lastInvokedAt = useRef<number>(0);
  const trailingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trailingArgs = useRef<TArgs | null>(null);

  useEffect(
    () => (): void => {
      if (trailingTimer.current !== null) clearTimeout(trailingTimer.current);
    },
    [],
  );

  return useCallback(
    (...args: TArgs): void => {
      const now = Date.now();
      const elapsed = now - lastInvokedAt.current;
      if (elapsed >= intervalMs) {
        lastInvokedAt.current = now;
        latestCallback.current(...args);
        return;
      }
      trailingArgs.current = args;
      if (trailingTimer.current !== null) return;
      trailingTimer.current = setTimeout(() => {
        trailingTimer.current = null;
        lastInvokedAt.current = Date.now();
        if (trailingArgs.current !== null) {
          const buffered = trailingArgs.current;
          trailingArgs.current = null;
          latestCallback.current(...buffered);
        }
      }, intervalMs - elapsed);
    },
    [intervalMs],
  );
}
